import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const ProfileHeader = ({ profile, currentLanguage }) => {
  const translations = {
    en: {
      verified: 'Verified',
      pending: 'Pending Verification',
      unverified: 'Not Verified',
      completedTrips: 'Completed Trips',
      memberSince: 'Member Since'
    },
    ko: {
      verified: '인증됨',
      pending: '인증 대기 중',
      unverified: '미인증',
      completedTrips: '완료된 여행',
      memberSince: '가입일'
    }
  };

  const t = translations?.[currentLanguage];

  const getVerificationStatus = () => {
    if (profile?.is_verified) {
      return { label: t?.verified, color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle' };
    }
    return { label: t?.unverified, color: 'text-muted-foreground', bg: 'bg-muted/30', icon: 'AlertCircle' };
  };

  const verificationStatus = getVerificationStatus();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return currentLanguage === 'en' ? date?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      : date?.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' });
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars?.push(<Icon key={i} name="Star" size={20} color="var(--color-warning)" fill="var(--color-warning)" />);
      } else if (i === fullStars && hasHalfStar) {
        stars?.push(<Icon key={i} name="StarHalf" size={20} color="var(--color-warning)" fill="var(--color-warning)" />);
      } else {
        stars?.push(<Icon key={i} name="Star" size={20} color="var(--color-muted)" />);
      }
    }
    return stars;
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-md overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Avatar */}
          <div className="flex-shrink-0">
            <div className="relative">
              <AppImage
                src={profile?.avatar_url || '/assets/images/no_image.png'}
                alt={`${profile?.full_name} profile photo`}
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-border"
              />
              {profile?.is_verified && (
                <div className="absolute bottom-0 right-0 w-8 h-8 md:w-10 md:h-10 bg-success rounded-full flex items-center justify-center border-4 border-card">
                  <Icon name="CheckCircle" size={16} color="white" />
                </div>
              )}
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  {profile?.full_name}
                </h1>
                
                {/* Verification Badge */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`flex items-center gap-1.5 px-3 py-1.5 ${verificationStatus?.bg} rounded-full`}>
                    <Icon name={verificationStatus?.icon} size={16} color={`var(--color-${profile?.is_verified ? 'success' : 'muted-foreground'})`} />
                    <span className={`text-sm font-medium ${verificationStatus?.color}`}>
                      {verificationStatus?.label}
                    </span>
                  </div>
                </div>

                {/* Rating */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center gap-1">
                    {renderStars(profile?.rating)}
                  </div>
                  <span className="text-lg font-semibold text-foreground">
                    {(profile?.rating || 0)?.toFixed(1)}
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="flex gap-6 md:gap-8">
                <div className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary">
                    {profile?.completed_trips || 0}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    {t?.completedTrips}
                  </div>
                </div>
              </div>
            </div>

            {/* Member Since */}
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Calendar" size={16} color="var(--color-muted-foreground)" />
                <span>{t?.memberSince}: {formatDate(profile?.created_at)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;